import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowUpRight, ShieldCheck, FileText, Atom, Scales,
  Drop, Pill, FlowerLotus, TestTube, EnvelopeSimple, LinkedinLogo,
  ChartLineUp, Scroll, Gavel, Database,
} from '@phosphor-icons/react';
import ToxicologyWatch from '../components/ToxicologyWatch';
import ScientificFrameworks from '../components/ScientificFrameworks';
import TI from '../components/brand/TI';
import AgMark from '../components/brand/AgMark';

/* ====================================================================
 * The Agyekum Group — Aᴳ consulting site (tagc.us)
 *
 * Content sourced from tagc.us. Editorial, slate + ivory + restrained
 * coral. Distinct identity from the ToxIntel product portal.
 * ==================================================================== */

const SLATE = '#0B1426';
const INK = '#1B2538';
const MUTED = '#5B6577';
const IVORY = '#F5F1E8';
const CORAL = '#FF4E6A';

function Mark() {
  return (
    <Link to="/tagc" className="flex items-center gap-3 group" data-testid="tagc-mark">
      <AgMark size={36} variant="light" />
      <span className="font-serif text-xl sm:text-2xl tracking-tight" style={{ color: SLATE }}>
        The Agyekum Group
      </span>
    </Link>
  );
}

function ServiceCard({ Icon, title, body, items = [] }) {
  return (
    <article
      className="border-t pt-6"
      style={{ borderColor: '#D6CDB8' }}
      data-testid={`service-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div
        className="w-10 h-10 rounded-sm flex items-center justify-center mb-5"
        style={{ backgroundColor: SLATE }}
      >
        <Icon size={20} color={IVORY} weight="duotone" />
      </div>
      <h3 className="font-serif text-xl mb-3 leading-tight" style={{ color: SLATE }}>{title}</h3>
      <p className="text-[13.5px] leading-relaxed mb-3" style={{ color: MUTED }}>{body}</p>
      {items.length > 0 && (
        <ul className="space-y-1.5 text-[12.5px]" style={{ color: INK }}>
          {items.map(item => (
            <li key={item} className="flex gap-2">
              <span style={{ color: CORAL }}>·</span><span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function PersonCard({ name, credentials, role, bio }) {
  return (
    <article
      className="border p-7 lg:p-8 rounded-sm"
      style={{ borderColor: '#D6CDB8', backgroundColor: '#FBF7EE' }}
      data-testid={`person-${name.split(' ')[0].toLowerCase()}`}
    >
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2" style={{ color: CORAL }}>
        {role}
      </p>
      <h3 className="font-serif text-2xl mb-1" style={{ color: SLATE }}>{name}</h3>
      <p className="text-[12px] font-mono uppercase tracking-wider mb-5" style={{ color: MUTED }}>
        {credentials}
      </p>
      <p className="text-[14px] leading-relaxed" style={{ color: INK }}>{bio}</p>
    </article>
  );
}

export default function TagcLanding() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'The Agyekum Group',
    alternateName: 'Aᴳ',
    url: 'https://tagc.us',
    logo: 'https://tagc.us/logo.png',
    sameAs: ['https://www.linkedin.com/company/the-agyekum-group/'],
    description:
      'Independent toxicological evaluation, exposure-based risk assessment, and regulatory intelligence for cosmetics, personal care, ingestibles, and consumer products.',
    knowsAbout: [
      'Toxicology',
      'Exposure-based risk assessment',
      'Cosmetic safety assessment',
      'FDA MoCRA',
      'EU Cosmetics Regulation 1223/2009',
      'California Proposition 65',
      'IFRA standards',
      'PFAS regulation',
      'Regulatory toxicology',
    ],
    founder: {
      '@type': 'Person',
      name: 'Augustine K. Agyekum, PhD',
      jobTitle: 'Independent Toxicologist',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'regulatory',
        email: 'regulatory@tagc.us',
        areaServed: ['US', 'EU', 'UK', 'CA'],
        availableLanguage: ['en'],
      },
    ],
    subOrganization: [
      {
        '@type': 'Organization',
        name: 'ToxIntel',
        url: 'https://toxintel.tagc.us',
        description:
          'Enterprise toxicology intelligence infrastructure — exposure assessment, contaminant surveillance, and compliance documentation.',
      },
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'The Agyekum Group',
    url: 'https://tagc.us',
    publisher: { '@type': 'Organization', name: 'The Agyekum Group' },
  };

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: IVORY, color: INK }}>
      <Helmet>
        <html lang="en" />
        <title>
          The Agyekum Group · Independent Toxicology &amp; Exposure-Based Risk
          Assessment
        </title>
        <meta
          name="description"
          content="Aᴳ is an independent toxicology consultancy. Exposure-based risk assessment, MoCRA / EU 1223 / Prop 65 / IFRA alignment, retailer compliance, and litigation-aware scientific governance — powered by ToxIntel, our enterprise intelligence platform."
        />
        <meta
          name="keywords"
          content="toxicology consulting, cosmetic safety assessment, exposure-based risk assessment, regulatory toxicology, MoCRA compliance, California Proposition 65, FDA cosmetics, EU Cosmetics Regulation 1223/2009, IFRA, PFAS, product safety, ToxIntel"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="canonical" href="https://tagc.us/" />
        {/* Aᴳ-branded favicon overrides the ToxIntel default for this route */}
        <link rel="icon" type="image/png" href="/tagc-favicon.png" />
        <link rel="apple-touch-icon" href="/tagc-favicon.png" />
        <link rel="shortcut icon" href="/tagc-favicon.ico" />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="The Agyekum Group" />
        <meta
          property="og:title"
          content="The Agyekum Group · Independent Toxicology &amp; Exposure-Based Risk Assessment"
        />
        <meta
          property="og:description"
          content="Independent toxicological evaluation and regulatory intelligence for cosmetics, personal care, fragrance, and ingestibles. Platform: ToxIntel."
        />
        <meta property="og:url" content="https://tagc.us/" />
        <meta property="og:image" content="https://tagc.us/tagc-og.png" />
        <meta property="og:image:secure_url" content="https://tagc.us/tagc-og.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="The Agyekum Group — Independent Toxicology & Exposure-Based Risk Assessment"
        />
        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="The Agyekum Group · Independent Toxicology"
        />
        <meta
          name="twitter:description"
          content="Exposure-based risk assessment, regulatory intelligence, and enterprise toxicology infrastructure (ToxIntel)."
        />
        <meta name="twitter:image" content="https://tagc.us/tagc-og.png" />
        <meta
          name="twitter:image:alt"
          content="The Agyekum Group — Independent Toxicology & Exposure-Based Risk Assessment"
        />
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>
      {/* Header */}
      <header className="border-b sticky top-0 z-30 backdrop-blur" style={{ borderColor: '#E3D9C0', backgroundColor: 'rgba(245,241,232,0.92)' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Mark />
          <nav className="hidden md:flex items-center gap-7 text-[13px] font-medium" style={{ color: INK }}>
            <a href="#scope" className="hover:text-[#FF4E6A] transition-colors">Scope</a>
            <a href="#services" className="hover:text-[#FF4E6A] transition-colors">Services</a>
            <a href="#frameworks" className="hover:text-[#FF4E6A] transition-colors">Frameworks</a>
            <a href="#philosophy" className="hover:text-[#FF4E6A] transition-colors">Philosophy</a>
            <a href="#team" className="hover:text-[#FF4E6A] transition-colors">Lead</a>
            <a
              href="https://toxintel.tagc.us"
              className="hover:text-[#FF4E6A] transition-colors"
              data-testid="tagc-nav-toxintel"
            >
              <TI />
            </a>
            <a href="#contact" className="hover:text-[#FF4E6A] transition-colors">Contact</a>
            <a
              href="https://toxintel.tagc.us"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border transition-colors hover:bg-[#0B1426] hover:text-[#F5F1E8]"
              style={{ borderColor: SLATE, color: SLATE }}
              data-testid="tagc-to-toxintel"
            >
              Platform Access <ArrowUpRight size={12} weight="bold" />
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <p
            className="text-[11px] font-mono uppercase tracking-[0.3em] mb-8"
            style={{ color: CORAL }}
            data-testid="tagc-eyebrow"
          >
            Independent toxicological evaluation &middot; Exposure-based risk
          </p>
          <h1
            className="font-serif leading-[0.95] tracking-tight mb-8"
            style={{ color: SLATE, fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            data-testid="tagc-hero-headline"
          >
            Scientific governance for beauty, personal care, and ingestible brands.
          </h1>
          <p className="text-lg lg:text-xl leading-relaxed max-w-3xl mb-6" style={{ color: MUTED }}>
            Exposure-based risk assessment, defensible substantiation, and regulatory alignment
            across U.S. and EU frameworks. The Agyekum Group (A<sup>G</sup>) conducts independent
            toxicological evaluations for cosmetics, personal care, fragrances, ingestibles, and
            consumer goods &mdash; documented for regulatory review, retail compliance, and
            enforcement-relevant decision-making.
          </p>
          <p
            className="text-sm leading-relaxed max-w-3xl"
            style={{ color: MUTED }}
            data-testid="tagc-hero-clients"
          >
            We work with <strong style={{ color: INK }}>startup, emerging, and enterprise</strong>{' '}
            companies across cosmetics, personal care, fragrance, and ingestible categories.
            Engagements are entered in a <em>selective advisory capacity</em> for brands,
            platforms, scientific initiatives, and product-safety governance programs.
          </p>

          <div className="mt-12">
            <div className="flex flex-wrap gap-3">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px"
                style={{ backgroundColor: SLATE, color: IVORY }}
                data-testid="tagc-cta-engage"
              >
                Engage the practice <ArrowUpRight size={14} weight="bold" />
              </a>
              <a
                href="#services"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider border transition-colors hover:bg-[#0B1426] hover:text-[#F5F1E8]"
                style={{ borderColor: SLATE, color: SLATE }}
                data-testid="tagc-cta-services"
              >
                Services
              </a>
            </div>
            <p
              className="mt-4 text-[12px] font-mono uppercase tracking-[0.2em]"
              style={{ color: MUTED }}
              data-testid="tagc-cta-toxintel-descriptor"
            >
              Platform access available below.
            </p>
          </div>
        </div>

        <aside className="lg:col-span-4 lg:pt-10">
          <div className="border-l-2 pl-6" style={{ borderColor: CORAL }}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: MUTED }}>
              Product Categories
            </p>
            <ul className="space-y-3 text-[14px]" style={{ color: SLATE }}>
              <li>Cosmetics &amp; Personal Care</li>
              <li>Ingestibles &amp; Supplements</li>
              <li>Fine Fragrance</li>
              <li>Ingredients &amp; Raw Materials</li>
              <li>Consumer Goods</li>
            </ul>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] mt-8 mb-3" style={{ color: MUTED }}>
              Regulatory Frameworks
            </p>
            <ul className="space-y-3 text-[14px]" style={{ color: SLATE }}>
              <li>FDA MoCRA</li>
              <li>EU Cosmetic Regulation (1223/2009)</li>
              <li>California Proposition 65</li>
              <li>IFRA standards</li>
              <li>PFAS &mdash; multi-jurisdiction</li>
            </ul>
          </div>
        </aside>
      </section>

      {/* Enterprise Toxicology Intelligence — dedicated ToxIntel band */}
      <section
        id="toxintel"
        className="border-t"
        style={{ borderColor: '#1B2538', backgroundColor: SLATE, color: IVORY }}
        data-testid="tagc-toxintel-band"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p
              className="text-[11px] font-mono uppercase tracking-[0.3em] mb-5"
              style={{ color: CORAL }}
              data-testid="tagc-toxintel-band-eyebrow"
            >
              Toxicology intelligence layer
            </p>
            <h2
              className="font-serif leading-[0.98] tracking-tight mb-6"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)' }}
              data-testid="tagc-toxintel-band-headline"
            >
              Toxicology Intelligence
            </h2>
            <p
              className="text-lg leading-relaxed max-w-2xl opacity-90 mb-8"
              data-testid="tagc-toxintel-band-subhead"
            >
              Exposure-based safety evaluation, regulatory intelligence,
              formulation review, and scientific risk assessment &mdash;
              delivered through <TI />, the Aᴳ platform for independent
              consultants, growing brands, manufacturers, retailers, and
              legal teams.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://toxintel.tagc.us"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px shadow-sm"
                style={{ backgroundColor: CORAL, color: SLATE }}
                data-testid="tagc-toxintel-band-launch"
              >
                Launch&nbsp;<TI /> <ArrowUpRight size={14} weight="bold" />
              </a>
              <a
                href="mailto:regulatory@tagc.us?subject=Platform%20Access%20Request%20%E2%80%94%20ToxIntel&body=Briefly%3A%20company%2C%20product%20categories%2C%20jurisdictions%2C%20and%20the%20regulatory%20question%20that%20prompted%20the%20call."
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider border transition-colors hover:bg-[#F5F1E8] hover:text-[#0B1426]"
                style={{ borderColor: IVORY, color: IVORY }}
                data-testid="tagc-toxintel-band-enterprise"
              >
                Request Platform Access
              </a>
            </div>
          </div>
          <aside className="lg:col-span-4 lg:col-start-9">
            <div
              className="border rounded-sm p-7"
              style={{ borderColor: 'rgba(245,241,232,0.18)', backgroundColor: 'rgba(245,241,232,0.04)' }}
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: CORAL }}>
                Platform capabilities
              </p>
              <ul className="space-y-3 text-[13.5px] opacity-90">
                <li>Exposure assessment (SED / MoS)</li>
                <li>Contaminant surveillance &amp; pathway detection</li>
                <li>Compliance Passport &mdash; retailer-readiness</li>
                <li>Margin-of-Safety modelling per population band</li>
                <li>Audit-ready documentation export</li>
              </ul>
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] mt-6 opacity-70">
                toxintel.tagc.us
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* Practice capabilities — five named expertise blocks */}
      <section
        id="capabilities"
        className="border-t"
        style={{ borderColor: '#E3D9C0' }}
        data-testid="tagc-capabilities"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-4">
              <p
                className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
                style={{ color: CORAL }}
              >
                Practice capabilities
              </p>
              <h2
                className="font-serif text-4xl lg:text-5xl leading-[1.05]"
                style={{ color: SLATE }}
              >
                Where Aᴳ is engaged.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
              <p className="text-base lg:text-lg leading-relaxed" style={{ color: MUTED }}>
                Five practice areas anchor every engagement. Each is signed by
                a named toxicologist and documented for the next reader &mdash;
                regulator, retailer, or court.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {[
              {
                Icon: ShieldCheck,
                title: 'Independent toxicological evaluation',
                body:
                  'Signed assessments of ingredient and finished-product safety. Hazard characterization, dose–response review, and uncertainty analysis structured for regulator and retailer scrutiny.',
              },
              {
                Icon: ChartLineUp,
                title: 'Exposure-based risk assessment',
                body:
                  'Quantitative SED / NOAEL / MoS / BMD analyses per population band. Pathway-specific exposure modelling for dermal, oral, inhalation, and mucosal use.',
              },
              {
                Icon: Drop,
                title: 'Cosmetic & consumer product safety',
                body:
                  'Product-level safety substantiation across cosmetics, personal care, fragrance, ingestibles, and adjacent consumer categories — fit for MoCRA, EU 1223, and IFRA contexts.',
              },
              {
                Icon: Gavel,
                title: 'Regulatory & litigation support',
                body:
                  'Toxicology-led regulatory strategy and litigation-aware documentation. Independent expert review, Prop 65 exposure framing, and enforcement-relevant scientific narrative.',
              },
              {
                Icon: Database,
                slug: 'enterprise-intelligence-via-toxintel',
                title: <>Toxicology intelligence via <TI /></>,
                body:
                  'Aᴳ\u2019s in-house platform — contaminant surveillance, formulation review, compliance passport export, and continuously updated regulatory context. Made available to enterprise clients.',
              },
              {
                Icon: Scroll,
                title: 'Continuous regulatory monitoring',
                body:
                  'Federal Register, FDA, CPSC, OEHHA, and FTC signals tracked and interpreted — surfaced below in Toxicology Watch and integrated into client briefings.',
              },
            ].map(({ Icon, title, body, slug }) => (
              <article
                key={slug || (typeof title === 'string' ? title : 'capability')}
                className="border-t pt-6"
                style={{ borderColor: '#D6CDB8' }}
                data-testid={`capability-${slug || (typeof title === 'string' ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'item')}`}
              >
                <div
                  className="w-10 h-10 rounded-sm flex items-center justify-center mb-5"
                  style={{ backgroundColor: SLATE }}
                >
                  <Icon size={20} color={IVORY} weight="duotone" />
                </div>
                <h3 className="font-serif text-xl mb-3 leading-tight" style={{ color: SLATE }}>
                  {title}
                </h3>
                <p className="text-[13.5px] leading-relaxed" style={{ color: MUTED }}>
                  {body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific frameworks — editorial methodology surface */}
      <ScientificFrameworks />

      {/* Scope strip */}
      <section
        id="scope"
        className="border-t"
        style={{ borderColor: '#E3D9C0', backgroundColor: '#FBF7EE' }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: CORAL }}>
              01 &mdash; Scope
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]" style={{ color: SLATE }}>
              Where the work is documented to support third-party scrutiny.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 grid sm:grid-cols-2 gap-x-8 gap-y-7">
            {[
              ['Regulatory & Governance', 'Toxicology and risk assessment aligned with EU, FDA, and international frameworks where safety substantiation and exposure analysis are required.'],
              ['Retail Compliance', 'Evaluation of formulations and ingredients against exposure-based safety criteria and documentation standards applied in retailer governance.'],
              ['Legal & Enforcement', 'Independent toxicological analysis of chemical exposure, dose–response, causation, and complex data — structured to withstand legal scrutiny.'],
              ['Ingredient & Raw Material', 'Hazard characterization, exposure considerations, and regulatory status review of ingredients in support of downstream use.'],
            ].map(([title, body]) => (
              <div key={title}>
                <h3 className="font-serif text-lg mb-2" style={{ color: SLATE }}>{title}</h3>
                <p className="text-[13.5px] leading-relaxed" style={{ color: MUTED }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="border-t" style={{ borderColor: '#E3D9C0' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-4">
              <p className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: CORAL }}>
                02 &mdash; Services
              </p>
              <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]" style={{ color: SLATE }}>
                Independent assessment, end to end.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
              <p className="text-base lg:text-lg leading-relaxed" style={{ color: MUTED }}>
                Every engagement is led personally, signed off in writing, and structured for
                regulatory inspection, retailer review, and enforcement-relevant decision-making.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            <ServiceCard
              Icon={Drop}
              title="Personal Care & Cosmetics"
              body="Independent toxicological evaluation and exposure-based risk assessment for cosmetic and personal care products across all categories."
              items={[
                'Ingredient & formula-level toxicology',
                'Margin of Safety (MoS) determinations',
                'U.S. and international framework alignment',
                'Claims & substantiation support',
              ]}
            />
            <ServiceCard
              Icon={Pill}
              title="Ingestibles & Supplements"
              body="Exposure-based safety evaluation and governance suitable for third-party review across dietary supplements and ingestible wellness products."
              items={[
                'NOAEL / LOAEL / TTC / BMD analysis',
                'Heavy metals & impurity context',
                'Pregnancy/breastfeeding population review',
                'Retailer-ready documentation',
              ]}
            />
            <ServiceCard
              Icon={FlowerLotus}
              title="Fine Fragrance"
              body="Exposure-based risk assessment for fragrance ingredients and finished blends, including sensitization potential and IFRA alignment."
              items={[
                'Ingredient-level hazard & exposure',
                'IFRA standards & use-level review',
                'Sensitisation & claim-relevant safety',
                'Single naturals to complex accords',
              ]}
            />
            <ServiceCard
              Icon={ShieldCheck}
              title="MoCRA Compliance"
              body="Defensible toxicological evaluation supporting MoCRA safety substantiation, adverse-event frameworks, and inspection-ready documentation."
              items={[
                'Safety substantiation dossiers',
                'Adverse-event reporting frameworks',
                'GMP-adjacent governance review',
                'Product listing & labeling context',
              ]}
            />
            <ServiceCard
              Icon={Scales}
              title="California Prop 65"
              body="Quantitative exposure assessment and regulatory strategy for Proposition 65–listed substances across consumer products."
              items={[
                'Pathway-specific exposure estimates',
                'NSRL / MADL safe-harbor comparison',
                'Migration & transfer interpretation',
                'Enforcement-defensible documentation',
              ]}
            />
            <ServiceCard
              Icon={Atom}
              title="PFAS Consultation"
              body="Exposure-driven safety assessment for brands navigating PFAS in consumer products and materials, multi-jurisdiction aware."
              items={[
                'Exposure characterization',
                'Toxicological reference values',
                'Retailer & state-level restrictions',
                'Documentation for third-party review',
              ]}
            />
            <ServiceCard
              Icon={TestTube}
              title="Testing Strategy & Oversight"
              body="Toxicology-first study design. Every protocol must reduce uncertainty and align with intended use conditions."
              items={[
                'HRIPT / RIPT / cumulative irritation',
                'Preservative efficacy testing (PET)',
                'Benzene / heavy metals / PFAS analytics',
                'SPF & claim substantiation support',
              ]}
            />
            <ServiceCard
              Icon={FileText}
              title="Ingredient Safety Review"
              body="Hazard characterization and risk-based screening of raw materials and functional components — including scrutinized substances."
              items={[
                'Sensitization & systemic exposure',
                'Restricted / monitored substance review',
                'Substitution & reformulation guidance',
                'Supplier & retailer communication',
              ]}
            />
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section
        id="philosophy"
        className="border-t"
        style={{ borderColor: '#1B2538', backgroundColor: SLATE, color: IVORY }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p
              className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
              style={{ color: CORAL }}
            >
              03 &mdash; Philosophy
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]">
              Testing does not create safety.<br />
              <span style={{ color: CORAL }}>Interpretation does.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-7">
            <p className="text-base lg:text-lg leading-relaxed opacity-90">
              Testing volume is increasing. The differentiator is not how much testing is performed
              &mdash; it is whether the data meaningfully reduces uncertainty and strengthens
              governance. Before recommending or reviewing any protocol, we define the decision:
            </p>
            <ul className="space-y-3 text-[15px] opacity-90">
              <li>&middot; What uncertainty are we resolving?</li>
              <li>&middot; What claim is being supported?</li>
              <li>&middot; What exposure assumptions govern the product?</li>
              <li>&middot; Would additional data materially change the safety conclusion?</li>
            </ul>
            <div
              className="mt-8 pl-8 border-l-2 italic font-serif text-xl leading-snug"
              style={{ borderColor: CORAL }}
              data-testid="tagc-quote"
            >
              &ldquo;Testing can be outsourced. Interpretation and documentation cannot.&rdquo;
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="border-t" style={{ borderColor: '#E3D9C0', backgroundColor: '#FBF7EE' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-4">
              <p className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: CORAL }}>
                04 &mdash; Practice lead
              </p>
              <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05]" style={{ color: SLATE }}>
                Every assessment is signed by a named toxicologist.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
              <p className="text-base lg:text-lg leading-relaxed" style={{ color: MUTED }}>
                No anonymous reports, no offshored interpretation. Aᴳ is led personally by
                Dr.&nbsp;Augustine&nbsp;K.&nbsp;Agyekum, with specialist advisors engaged
                per&nbsp;assignment.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <PersonCard
              role="Founder · Independent Toxicologist"
              name='Augustine K. Agyekum, PhD'
              credentials='"Dr. Kwaku"'
              bio="Founder of Aᴳ, an independent scientific consulting firm specialising in cosmetic and consumer product safety. Dr. Agyekum advises brands, manufacturers, and legal teams on ingredient safety, quantitative risk assessment, and compliance with FDA MoCRA, the EU Cosmetics Regulation, IFRA standards, and California Prop 65 — translating complex toxicological data into clear safety decisions."
            />
            <div
              className="border border-dashed p-8 lg:p-10 rounded-sm flex flex-col justify-center"
              style={{ borderColor: '#D6CDB8', backgroundColor: 'transparent' }}
            >
              <p className="text-[11px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: CORAL }}>
                Advisory roster
              </p>
              <p className="text-base leading-relaxed" style={{ color: MUTED }}>
                Aᴳ engages specialist advisors on a per-assignment basis &mdash; drawing on
                board-certified toxicologists, regulatory scientists, and analytical chemists
                when an engagement warrants additional depth. Roster details available on request.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-t" style={{ borderColor: '#E3D9C0' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4" style={{ color: CORAL }}>
              05 &mdash; Engage
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] mb-6" style={{ color: SLATE }}>
              We take a small number of new engagements each quarter.
            </h2>
            <p className="text-base leading-relaxed" style={{ color: MUTED }}>
              Send a short brief &mdash; product, jurisdictions, timeline, and the regulatory
              question that prompted the call. We respond within two business days.
            </p>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <div className="border p-8 lg:p-10 rounded-sm" style={{ borderColor: '#D6CDB8', backgroundColor: '#FBF7EE' }}>
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] mb-2" style={{ color: MUTED }}>
                Direct line
              </p>
              <a
                href="mailto:regulatory@tagc.us"
                className="flex items-center gap-2 font-serif text-2xl lg:text-3xl mb-3 hover:text-[#FF4E6A] transition-colors"
                style={{ color: SLATE }}
                data-testid="tagc-email"
              >
                <EnvelopeSimple size={22} weight="duotone" /> regulatory@tagc.us
              </a>
              <a
                href="https://www.linkedin.com/company/the-agyekum-group/"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[13px] mb-8 hover:text-[#FF4E6A] transition-colors"
                style={{ color: INK }}
                data-testid="tagc-linkedin"
              >
                <LinkedinLogo size={16} weight="duotone" /> linkedin.com/company/the-agyekum-group
              </a>

              <div className="grid sm:grid-cols-2 gap-6 text-[13px]" style={{ color: INK }}>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
                    Practice
                  </p>
                  <p>The Agyekum Group (A<sup>G</sup>)</p>
                  <p>Independent toxicology</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
                    Engagements
                  </p>
                  <p>Cosmetics &middot; Personal care</p>
                  <p>Ingestibles &middot; Fragrance</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
                    Jurisdictions
                  </p>
                  <p>EU &middot; UK &middot; US (FDA / MoCRA)</p>
                  <p>California Prop 65 &middot; APAC</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
                    Platform
                  </p>
                  <a
                    href="https://toxintel.tagc.us"
                    className="hover:text-[#FF4E6A] transition-colors"
                    data-testid="tagc-footer-toxintel"
                  >
                    Platform Access &rarr;
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Toxicology Watch — public regulatory feed */}
      <ToxicologyWatch />

      {/* Ecosystem architecture — subtle three-pillar architecture strip */}
      <section
        id="ecosystem"
        className="border-t"
        style={{ borderColor: '#E3D9C0', backgroundColor: '#FBF7EE' }}
        data-testid="tagc-ecosystem"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
          <p
            className="text-[10px] font-mono uppercase tracking-[0.3em] mb-10"
            style={{ color: MUTED }}
            data-testid="tagc-ecosystem-eyebrow"
          >
            Ecosystem architecture
          </p>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-10">
            {/* Tier 1 — Aᴳ authority */}
            <div className="border-t pt-6" style={{ borderColor: SLATE }} data-testid="ecosystem-tagc">
              <p
                className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2"
                style={{ color: CORAL }}
              >
                Authority
              </p>
              <h3 className="font-serif text-xl mb-2" style={{ color: SLATE }}>
                A<sup className="text-[10px]">G</sup> Consulting
              </h3>
              <p className="text-[13px] leading-relaxed mb-3" style={{ color: MUTED }}>
                Independent scientific governance, toxicological evaluation, and regulatory
                interpretation — signed by a named toxicologist.
              </p>
              <p className="text-[12px] font-mono" style={{ color: INK }}>tagc.us</p>
            </div>

            {/* Tier 2 — ToxIntel enterprise platform */}
            <div className="border-t pt-6" style={{ borderColor: SLATE }} data-testid="ecosystem-toxintel">
              <p
                className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2"
                style={{ color: CORAL }}
              >
                Enterprise infrastructure
              </p>
              <h3 className="font-serif text-xl mb-2" style={{ color: SLATE }}>
                <TI /> Platform
              </h3>
              <p className="text-[13px] leading-relaxed mb-3" style={{ color: MUTED }}>
                Enterprise toxicology intelligence infrastructure — exposure assessment,
                contaminant surveillance, and compliance documentation for brands and retailers.
              </p>
              <a
                href="https://toxintel.tagc.us"
                className="inline-flex items-center gap-1 text-[12px] font-mono hover:text-[#FF4E6A] transition-colors"
                style={{ color: INK }}
                data-testid="ecosystem-toxintel-link"
              >
                toxintel.tagc.us <ArrowUpRight size={11} weight="bold" />
              </a>
            </div>

            {/* Tier 3 — GlowCheck consumer (deliberately understated) */}
            <div className="border-t pt-6" style={{ borderColor: '#D6CDB8' }} data-testid="ecosystem-glowcheck">
              <p
                className="text-[10px] font-mono uppercase tracking-[0.25em] mb-2"
                style={{ color: MUTED }}
              >
                Consumer distribution
              </p>
              <h3 className="font-serif text-xl mb-2" style={{ color: SLATE }}>
                GlowCheck Consumer App
              </h3>
              <p className="text-[13px] leading-relaxed mb-3" style={{ color: MUTED }}>
                Consumer-facing distribution layer of the broader Aᴳ ecosystem. Operated and
                positioned separately from enterprise infrastructure.
              </p>
              <a
                href="https://getglowcheck.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] font-mono hover:text-[#FF4E6A] transition-colors"
                style={{ color: MUTED }}
                data-testid="ecosystem-glowcheck-link"
              >
                getglowcheck.app <ArrowUpRight size={11} weight="bold" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: '#E3D9C0' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="text-[12px]" style={{ color: MUTED }}>
            &copy; {new Date().getFullYear()} The Agyekum Group (A<sup>G</sup>). All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px]" style={{ color: MUTED }}>
            <span>tagc.us</span>
            <a
              href="mailto:regulatory@tagc.us"
              className="hover:text-[#FF4E6A] transition-colors"
              data-testid="tagc-footer-email"
            >
              regulatory@tagc.us
            </a>
            <a
              href="https://toxintel.tagc.us"
              className="inline-flex items-center gap-1 hover:text-[#FF4E6A] transition-colors"
              data-testid="tagc-footer-platform-access"
            >
              Platform Access <ArrowUpRight size={10} weight="bold" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
